import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';

export interface CardOpenOptions {
  /** Stable agent id, e.g. "social.accounts.card.<id>" */
  agentId: string;
  /** Accessible label describing what opens, e.g. "Open details for @handle" */
  label: string;
  /** Called when the card is activated by click or Enter/Space. */
  onOpen: () => void;
}

/**
 * Props that turn an entire card into an accessible, keyboard-operable button
 * that opens a detail view. Spread onto a `<Card>` (or any div):
 *
 *   <Card {...cardOpen({ agentId, label, onOpen: () => setDetail(item) })}>…</Card>
 *
 * Note: inner interactive controls should call `e.stopPropagation()` in their
 * own handlers so they do not also trigger the detail opener.
 */
export function cardOpen({ agentId, label, onOpen }: CardOpenOptions) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': label,
    'data-agent-id': agentId,
    onClick: (e: ReactMouseEvent) => {
      e.stopPropagation();
      onOpen();
    },
    onKeyDown: (e: ReactKeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onOpen();
      }
    },
  };
}

export default cardOpen;
